import { PrototypeApiError } from '../apiClient';
import { clearPasswordResetToken, readPasswordResetToken } from '../authTokenUrl';
import { byId, inputValue } from '../domUtils';
import { closeModal, openModal, showView } from '../navigationController';
import { recordRuntimeError } from '../prototypeRuntimeSupport';
import type { PrototypeRuntime, Root } from '../prototypeTypes';
import { validatePassword, validatePasswordMatch } from '../validation';
import { clearAuthModalFields } from '../formProfileController';

export async function handleResetPassword(root: Root, runtime: PrototypeRuntime): Promise<void> {
	paintResetAlert(root, null);
	const password = inputValue(root, 'resetNewPassword');
	const validationError = validatePassword(password) ?? validatePasswordMatch(password, inputValue(root, 'resetConfirmPassword'));
	if (validationError) return paintResetAlert(root, validationError);
	const token = readPasswordResetToken();
	if (!token) return showTerminalResetError(root);

	const form = byId(root, 'resetPasswordForm');
	const loading = byId(root, 'resetPasswordLoading');
	if (form) form.style.display = 'none';
	if (loading) loading.style.display = 'block';
	try {
		const result = await runtime.api.resetPassword({ token, password });
		clearPasswordResetToken();
		if (loading) loading.style.display = 'none';
		const success = byId(root, 'resetPasswordSuccess');
		paintResetSuccess(root, result.purpose);
		if (success) success.style.display = 'block';
		clearAuthModalFields(root, 'resetPassword');
		setTimeout(() => {
			closeModal(root, 'resetPasswordOverlay');
			showView('home', root);
			openModal(root, 'loginModal');
			window.history.replaceState({}, '', '/');
		}, 2000);
	} catch (error) {
		recordRuntimeError(runtime, error);
		if (isTerminalTokenFailure(error)) {
			clearPasswordResetToken();
			showTerminalResetError(root);
			return;
		}
		if (loading) loading.style.display = 'none';
		if (form) form.style.display = '';
		paintResetAlert(root, retryableResetMessage(error));
	}
}

function paintResetSuccess(root: Root, purpose: 'reset_password' | 'set_password'): void {
	if (purpose !== 'set_password') return;
	const title = byId(root, 'resetPasswordSuccessTitle');
	const detail = byId(root, 'resetPasswordSuccessDetail');
	if (title) title.textContent = 'Password set!';
	if (detail) detail.textContent = 'You can now log in with Google or your new password. Redirecting to login...';
}

function isTerminalTokenFailure(error: unknown): boolean {
	return error instanceof PrototypeApiError
		&& error.status === 400
		&& (error.body as { error?: string } | null)?.error === 'invalid_or_expired_reset_token';
}

function retryableResetMessage(error: unknown): string {
	if (!(error instanceof PrototypeApiError)) return 'Password reset failed. Please try again.';
	if (error.status === 0) return 'Connection failed. Check your connection, then try again.';
	if (error.status === 429) return 'Too many attempts. Please wait a minute, then try again.';
	if (error.status === 400) return 'That password could not be accepted. Check the requirements, then try again.';
	return 'Password reset is temporarily unavailable. Please try again.';
}

function paintResetAlert(root: Root, message: string | null): void {
	const alert = byId(root, 'resetPasswordFormAlert');
	if (!alert) return;
	alert.textContent = message ?? '';
	alert.hidden = !message;
}

function showTerminalResetError(root: Root): void {
	const form = byId(root, 'resetPasswordForm');
	const loading = byId(root, 'resetPasswordLoading');
	const error = byId(root, 'resetPasswordError');
	if (form) form.style.display = 'none';
	if (loading) loading.style.display = 'none';
	if (error) error.style.display = 'block';
}
